import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { documentsApi, Document } from '../services/documentsApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Documents: React.FC = () => {
    const { t } = useLanguage();
    
    const [documents, setDocuments] = useState<Document[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
    };

    const [formData, setFormData] = useState<Partial<Document>>({
        vehIdf: 0,
        drvIdf: 0,
        type: 'insurance',
        title: '',
        expiryDate: new Date()
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [docsRes, vehiclesRes, driversRes] = await Promise.all([
                documentsApi.getDocuments(),
                vehiclesApi.getAll(),
                driversApi.getAll()
            ]);

            if (vehiclesRes.success) {
                setVehicles(vehiclesRes.data);
            }
            if (driversRes.success) {
                setDrivers(driversRes.data);
            }
            setDocuments(docsRes);
        } catch (error) {
            console.error('Failed to load data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleOpenDialog = (doc?: Document) => {
        if (doc) {
            setEditingDocument(doc);
            setFormData({
                ...doc
            });
        } else {
            setEditingDocument(null);
            setFormData({
                vehIdf: 0,
                drvIdf: 0,
                type: 'insurance',
                title: '',
                expiryDate: new Date()
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            // Clean up empty 0 IDs to prevent FK constraint errors
            const submitData = { ...formData };
            if (submitData.vehIdf === 0) submitData.vehIdf = undefined;
            if (submitData.drvIdf === 0) submitData.drvIdf = undefined;

            let response;
            if (editingDocument) {
                response = await documentsApi.updateDocument(editingDocument.id, submitData);
            } else {
                response = await documentsApi.createDocument(submitData);
            }

            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            setIsDialogOpen(false);
            loadData();
        } catch (error) {
            console.error('Error saving document:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm'))) return;
        try {
            await documentsApi.deleteDocument(id);
            blobticMessage.success(t('common.messages.success'), t('common.messages.deleted'));
            loadData();
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'title', label: t('parc.documents.docTitle'), minWidth: 150 },
        {
            id: 'type',
            label: t('parc.documents.type'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'insurance') return t('parc.documents.typeInsurance');
                if (value === 'technical_control') return t('parc.documents.typeTechControl');
                if (value === 'registration') return t('parc.documents.typeRegistration');
                if (value === 'contract') return t('parc.documents.typeContract');
                if (value === 'other') return t('parc.documents.typeOther');
                return value;
            }
        },
        { id: 'vehRegistration', label: t('parc.documents.vehicle'), minWidth: 120 },
        { id: 'drvLicenseNumber', label: t('parc.documents.driver'), minWidth: 120 },
        { id: 'expiryDate', label: t('parc.documents.expiryDate'), minWidth: 100, format: (value: Date | string) => value ? new Date(value).toLocaleDateString() : '' }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.documents.newDocument')}
        </Button>
    );

    return (
        <GenericPage title={t('parc.documents.title')} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={documents}
                onEdit={handleOpenDialog}
                onDelete={(d: any) => handleDelete(d.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingDocument ? t('parc.documents.editDocument') : t('parc.documents.newDocument')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('parc.documents.docTitle')}
                                value={formData.title || ''}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.documents.type')}</InputLabel>
                                <Select
                                    value={formData.type || 'insurance'}
                                    label={t('parc.documents.type')}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                >
                                    <MenuItem value="insurance">{t('parc.documents.typeInsurance')}</MenuItem>
                                    <MenuItem value="technical_control">{t('parc.documents.typeTechControl')}</MenuItem>
                                    <MenuItem value="registration">{t('parc.documents.typeRegistration')}</MenuItem>
                                    <MenuItem value="contract">{t('parc.documents.typeContract')}</MenuItem>
                                    <MenuItem value="other">{t('parc.documents.typeOther')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('parc.documents.expiryDate')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.expiryDate)}
                                onChange={(e) => setFormData({ ...formData, expiryDate: new Date(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.documents.assignVehicle')}</InputLabel>
                                <Select
                                    value={formData.vehIdf || 0}
                                    label={t('parc.documents.assignVehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehIdf: Number(e.target.value) })}
                                >
                                    <MenuItem value={0}>{t('common.labels.none')}</MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.documents.assignDriver')}</InputLabel>
                                <Select
                                    value={formData.drvIdf || 0}
                                    label={t('parc.documents.assignDriver')}
                                    onChange={(e) => setFormData({ ...formData, drvIdf: Number(e.target.value) })}
                                >
                                    <MenuItem value={0}>{t('common.labels.none')}</MenuItem>
                                    {drivers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.licenseNumber}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Documents;
